import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { createOverlayComp, OverlayCompProps } from '../common/util/createOverlayComp';
import { ToastBar } from '../ToastBar/ToastBar';

const AnimatedToastBar = motion(ToastBar);

interface ToastComponentProps {
  message?: string | React.ReactNode;
  /**
   *  @description 등장 시간
   * @default 1500ms
   */
  time?: number;
  style?: React.CSSProperties;
  footerRef?: React.RefObject<HTMLDivElement> | null;
}

function ToastComponent({
  message,
  resolve,
  dispose,
  time = 1500,
  style,
  footerRef,
}: ToastComponentProps & OverlayCompProps) {
  const [active, setActive] = useState(true);
  const [bottom, setBottom] = useState<number | undefined>(style?.bottom as number | undefined);

  useEffect(() => {
    if (!footerRef?.current) {
      return;
    }

    const updateBottom = () => {
      if (footerRef.current) {
        const computedStyle = window.getComputedStyle(footerRef.current);
        const paddingBottom = parseInt(computedStyle.paddingBottom) || 0;
        const contentHeight = footerRef.current.clientHeight - paddingBottom;

        setBottom(Math.max(contentHeight, 0) + 8);
      }
    };

    updateBottom();

    // ResizeObserver로 높이 변경 감지
    const resizeObserver = new ResizeObserver(() => {
      updateBottom();
    });

    resizeObserver.observe(footerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [footerRef]);

  const finalStyle = {
    ...style,
    bottom: bottom ?? style?.bottom,
  };

  return (
    <AnimatePresence>
      {active && (
        <AnimatedToastBar
          message={message}
          style={finalStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onAnimationComplete={(value: any) => {
            if (value?.opacity === 1) {
              setTimeout(() => {
                setActive(false);
              }, time);
            }
            if (value?.opacity === 0) {
              resolve?.('');
              dispose?.();
            }
          }}
        />
      )}
    </AnimatePresence>
  );
}

interface ToastMessage {
  message: string | React.ReactNode;
  time?: number;
  style?: React.CSSProperties;
  footerRef?: React.RefObject<HTMLDivElement> | null;
}

export function renderToast(props: ToastMessage) {
  return ({ resolve, dispose }: OverlayCompProps) => {
    return <ToastComponent {...{ ...props, resolve, dispose }} />;
  };
}

export type ConfirmReturnValue = true;
const openToast = createOverlayComp<string, ToastMessage>(renderToast);

export class Toast {
  static show(
    message: string | React.ReactNode,
    time?: number,
    style?: React.CSSProperties,
    footerRef?: React.RefObject<HTMLDivElement> | null,
  ) {
    return openToast({ message, time, style, footerRef });
  }
}
